import { useCallback, useEffect, useState, useRef } from "react";
import { observer } from "mobx-react-lite";
import { ChevronDown, ChevronRight } from "lucide-react";

import { useTran } from "@/hooks";
import { Badge } from "@/components/ui/badge";
import { useOrganizationStore } from "../hooks";
import { getUnitTypeMeta, isLevelAbove } from "../utils/organization.helpers";
import OrgStatusTag from "./OrgStatusTag";

const INDENT_SIZE = 24;

const getCollapseKey = (node) => {
  if (!node) return null;
  const baseId = node.id || node._id || node.key || node.name;
  if (!baseId) return null;
  if (node.groupKey) {
    return `${node.groupKey}:${baseId}`;
  }
  return baseId;
};

const normalizeCountKey = (value) => {
  const raw = value?.toString?.() ?? value;
  if (typeof raw !== "string") return "";
  return raw.trim().toLowerCase();
};

const readCountValue = (countById, normalizedKey) => {
  if (!countById || !normalizedKey) return undefined;

  if (typeof countById.get === "function") {
    return countById.get(normalizedKey);
  }

  if (typeof countById === "object") {
    return countById[normalizedKey];
  }

  return undefined;
};

const EmptyState = () => {
  const { t } = useTran();
  return (
    <div className="flex h-full flex-col items-center justify-center rounded-radius border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
      <p className="font-medium">{t("No units configured yet.")}</p>
      <p className="mt-1 text-xs">
        {t("Add your parent company and organization levels to get started.")}
      </p>
    </div>
  );
};

const resolveNodeCount = (countById, nodeId) => {
  const normalizedId = normalizeCountKey(nodeId);
  if (!normalizedId) return 0;

  const value = readCountValue(countById, normalizedId);
  if (typeof value === "number") return value;

  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const TopOptionRow = ({ option, selectedId, onSelect }) => {
  if (!option?.id) return null;

  const isActive = selectedId === option.id;
  const countValue = Number(option.count);
  const count = Number.isFinite(countValue) ? countValue : null;

  return (
    <button
      type="button"
      onClick={() => onSelect?.(option.id)}
      className={`relative flex w-full items-center gap-2 rounded-radius border border-transparent px-3 py-2 text-left transition ${
        isActive ? "bg-primary/10 text-primary" : "hover:bg-muted/60"
      }`}
    >
      <span className="flex-1 text-sm font-semibold text-foreground">
        {option.label}
      </span>
      {typeof count === "number" ? (
        <Badge
          variant="outline"
          className="border-border bg-background text-[11px] text-muted-foreground"
        >
          {count}
        </Badge>
      ) : null}
    </button>
  );
};

const TreeNode = ({
  node,
  depth,
  selectedId,
  onSelect,
  collapsedNodes,
  onToggleCollapse,
  countById,
  showReportCount = false,
}) => {
  const { t } = useTran();
  const meta = getUnitTypeMeta(node.unitLevel);
  const Icon = meta.icon;
  const isActive = node.id === selectedId;
  const isGroup = Boolean(node.isGroup);
  const indentation = depth * INDENT_SIZE;
  const hasChildren = node.children?.length > 0;
  const collapseKey = getCollapseKey(node);
  const isCollapsed = collapseKey ? collapsedNodes?.has(collapseKey) : false;
  const locationLabel =
    node.locationLabel || node.countryLabel || node.country || node.regionLabel;
  const reportCount =
    showReportCount && !isGroup ? resolveNodeCount(countById, node.id) : null;

  const handleToggleCollapse = (event) => {
    event.stopPropagation();
    if (!collapseKey) return;
    onToggleCollapse?.(collapseKey);
  };

  const handleSelect = () => {
    if (isGroup) return;
    onSelect?.(node.id);
  };

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={handleSelect}
        aria-disabled={isGroup}
        className={`relative flex w-full min-w-0 flex-col gap-1 rounded-radius border border-transparent px-3 py-2 text-left transition ${
          isActive ? "bg-primary/10 text-primary" : "hover:bg-muted/60"
        }`}
        style={{ paddingLeft: indentation }}
      >
        <div className="flex min-w-0 items-start gap-2">
          {hasChildren ? (
            <span
              role="button"
              aria-label={isCollapsed ? "Expand unit" : "Collapse unit"}
              tabIndex={-1}
              onClick={handleToggleCollapse}
              className="flex h-5 w-5 items-center justify-center rounded border border-border bg-background text-muted-foreground transition hover:bg-muted ml-2"
            >
              {isCollapsed ? (
                <ChevronRight className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
            </span>
          ) : (
            <span className="h-5 w-5" />
          )}
          <span
            className={`flex h-8 w-8 items-center justify-center rounded-full bg-background shadow-sm ${meta.color}`}
          >
            <Icon className="h-4 w-4" />
          </span>
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="break-words text-sm font-semibold text-foreground">
              {node.name}
            </span>
            {node.code && !isLevelAbove(node.unitLevel, 2) ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className={`break-words text-xs ${meta.color}`}>
                  {t("Code")}: {t(node.code)}
                </span>
                {!isGroup ? (
                  <OrgStatusTag
                    status={node.status}
                    inactiveDate={node.inactiveDate}
                  />
                ) : null}
              </div>
            ) : !isGroup ? (
              <OrgStatusTag
                status={node.status}
                inactiveDate={node.inactiveDate}
              />
            ) : null}
          </div>
          {locationLabel && node.unitLevel === "level_2" ? (
            <Badge
              variant="outline"
              className="max-w-full border-primary/30 bg-background text-[11px] text-primary"
            >
              {locationLabel}
            </Badge>
          ) : null}
          {showReportCount && typeof reportCount === "number" ? (
            <Badge
              variant="outline"
              className="border-border bg-background text-[11px] text-muted-foreground"
            >
              {reportCount}
            </Badge>
          ) : null}
        </div>
      </button>

      {hasChildren && !isCollapsed
        ? (node.children || []).map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              onSelect={onSelect}
              collapsedNodes={collapsedNodes}
              onToggleCollapse={onToggleCollapse}
              countById={countById}
              showReportCount={showReportCount}
            />
          ))
        : null}
    </div>
  );
};

const OrganizationTree = observer(
  ({
    tree: treeOverride,
    headerActions,
    countById = null,
    selectedId: selectedIdOverride,
    onSelect: onSelectOverride,
    topOption = null,
    showReportCount = false,
  }) => {
    const { t } = useTran();
    const {
      tree: storeTree,
      selectedId,
      selectUnit,
      loading,
    } = useOrganizationStore();
    const selectedOrgId = selectedIdOverride ?? selectedId;
    const handleSelectUnit = onSelectOverride ?? selectUnit;
    const tree = treeOverride ?? storeTree;
    const [collapsedNodes, setCollapsedNodes] = useState(() => new Set());

    const knownNodeIdsRef = useRef(new Set());

    useEffect(() => {
      if (!Array.isArray(tree)) return;

      const defaultCollapsedTypes = ["level_1", "business_type", "country"];

      const newlySeenIds = [];
      const walk = (nodes = []) => {
        nodes.forEach((node) => {
          const id = getCollapseKey(node);
          if (!id) return;

          if (!knownNodeIdsRef.current.has(id)) {
            knownNodeIdsRef.current.add(id);
            newlySeenIds.push(id);
          }

          if (Array.isArray(node.children)) walk(node.children);
        });
      };

      walk(tree);

      if (newlySeenIds.length === 0) return;

      setCollapsedNodes((prev) => {
        const next = new Set(prev);

        const markNewDefaults = (nodes = []) => {
          nodes.forEach((node) => {
            const id = getCollapseKey(node);
            if (!id) return;

            const isNew = newlySeenIds.includes(id);
            const shouldDefaultCollapse =
              isNew &&
              defaultCollapsedTypes.includes(node.unitLevel) &&
              node.children?.length;

            if (shouldDefaultCollapse) next.add(id);

            if (Array.isArray(node.children)) markNewDefaults(node.children);
          });
        };

        markNewDefaults(tree);
        return next;
      });
    }, [tree]);

    const handleToggleCollapse = useCallback((nodeId) => {
      setCollapsedNodes((prev) => {
        const next = new Set(prev);
        if (next.has(nodeId)) {
          next.delete(nodeId);
        } else {
          next.add(nodeId);
        }
        return next;
      });
    }, []);

    return (
      <div className="flex h-full w-full flex-col rounded-radius border border-border bg-card shadow-sm">
        <div className="flex flex-col gap-3 border-b border-border px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-base font-semibold text-foreground">
              {t("Organization Tree")}
            </p>
            <p className="text-xs text-muted-foreground">
              {tree?.length
                ? t("Select a unit to view its details.")
                : t("Awaiting initial setup.")}
            </p>
          </div>
          {headerActions ? (
            <div className="flex w-full items-start sm:w-auto">
              {headerActions}
            </div>
          ) : null}
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={index}
                  className="h-14 w-full animate-pulse rounded-radius bg-muted"
                />
              ))}
            </div>
          ) : tree?.length ? (
            <div className="space-y-1">
              {topOption ? (
                <TopOptionRow
                  option={topOption}
                  selectedId={selectedOrgId}
                  onSelect={handleSelectUnit}
                />
              ) : null}
              {tree.map((node) => (
                <TreeNode
                  key={node.id}
                  node={node}
                  depth={0}
                  selectedId={selectedOrgId}
                  onSelect={handleSelectUnit}
                  collapsedNodes={collapsedNodes}
                  onToggleCollapse={handleToggleCollapse}
                  countById={countById}
                  showReportCount={showReportCount}
                />
              ))}
            </div>
          ) : (
            <EmptyState />
          )}
        </div>
      </div>
    );
  },
);

export default OrganizationTree;
